import keyBy from 'lodash/keyBy'
import React, { createContext, useContext, useEffect, useMemo } from 'react'

import { useCSDetailsURLData } from 'api/configured-service/useCSDetailsURLData'
import {
  useLazyGetConfiguredServiceVersionsQuery,
  useLazyGetServiceEventsVersionsQuery,
  useLazyGetTagRefsVersionsQuery,
  useLazyGetWorkOrderVersionsByConfiguredServiceQuery,
} from 'api/entity-version/entityVersionCrud'
import { useLazyFetchMetaFiltersQuery } from 'api/meta/getMetaFilters'
import {
  selectMaterials,
  selectMeasures,
  selectMethods,
  selectPricingPeriods,
  selectPricingZones,
  selectRecurrenceOptions,
  selectServiceActions,
  selectTaxZones,
} from 'data/meta/metaSelectors'
import {
  formatAction,
  formatBoolean,
  formatDateOnly,
  formatDefault,
  formatInvoiceStatus,
  formatMaterial,
  formatMeasure,
  formatMethod,
  formatMoney,
  formatPricingPeriod,
  formatPricingZone,
  formatTaxZone,
} from 'utils/activity/activityFormatters'
import ActivityTreeBuilder, {
  type ActivityRow,
  type CustomBuilderFn,
  type RevisionLike,
  type TreePostProcessor,
} from 'utils/activity/ActivityTreeBuilder'
import recurringDaysUnionBuilder from 'utils/activity/customBuilders/recurringDaysUnionBuilder'
import serviceEventsBuilder from 'utils/activity/customBuilders/serviceEventsBuilder'
import tagRefBuilder from 'utils/activity/customBuilders/tagRefBuilder'
import workOrderInvoiceStatusBuilder from 'utils/activity/customBuilders/workOrderInvoiceStatusBuilder'
import { getRecurrenceText } from 'utils/service'

type ActivityLookups = {
  measures: unknown
  methods: unknown
  materials: unknown
  actions: unknown
  pricingZones: unknown
  pricingPeriods: unknown
  taxZones: unknown
}

// Only formatterRegistry is universal; the rest is provider-specific (the work-order activity tab
// renders its own grid and has no meta lookups).
type ActivityContextValue = {
  treeData?: ActivityRow[]
  isLoading?: boolean
  lookups?: ActivityLookups
  formatterRegistry: Record<string, unknown>
}

type ActivityProviderProps = {
  serviceId?: string
  children: React.ReactNode
}

type ServiceVersionRow = RevisionLike & {
  recurrenceId?: string | number | null
  recurrenceFrequency?: unknown
  status?: string | null
}

export const ActivityContext = createContext<ActivityContextValue | null>(null)

export const useActivity = (): ActivityContextValue | null => {
  const ctx = useContext(ActivityContext)
  // Be permissive for shared renderers that may be used outside this provider
  // (they will fall back to default formatting when context is missing)
  return ctx || null
}

const ActivityProvider = ({ serviceId: serviceIdProp, children }: ActivityProviderProps) => {
  const { id: urlId } = useCSDetailsURLData()
  const id = serviceIdProp ?? urlId

  const [fetchMetaFilters, { data: metaFiltersData, isLoading: isMetaLoading }] = useLazyFetchMetaFiltersQuery()
  const [getCSVersions, { data: csVersionsData, isLoading: isCSVLoading }] = useLazyGetConfiguredServiceVersionsQuery()
  const [getEventsVersions, { isLoading: isEventsDataLoading, data: eventsVersionData }] = useLazyGetServiceEventsVersionsQuery()
  const [getTagRefsVersions, { isLoading: isTagsLoading, data: tagRefsData }] = useLazyGetTagRefsVersionsQuery()
  const [getWorkOrderVersions, { isLoading: isWorkOrdersLoading, data: workOrderVersionsData }] =
    useLazyGetWorkOrderVersionsByConfiguredServiceQuery()

  useEffect(() => {
    fetchMetaFilters({ body: {} })
  }, [fetchMetaFilters])

  // One effect: all four share this trigger and argument, so copies only invite a fifth that drifts.
  useEffect(() => {
    if (!id) return
    getCSVersions({ id })
    getEventsVersions({ id })
    getTagRefsVersions({ id })
    getWorkOrderVersions({ id })
  }, [id, getCSVersions, getEventsVersions, getTagRefsVersions, getWorkOrderVersions])

  const measures = useMemo(() => selectMeasures(metaFiltersData), [metaFiltersData])
  const methods = useMemo(() => selectMethods(metaFiltersData), [metaFiltersData])
  const materials = useMemo(() => selectMaterials(metaFiltersData), [metaFiltersData])
  const actions = useMemo(() => selectServiceActions(metaFiltersData), [metaFiltersData])
  const pricingZones = useMemo(() => selectPricingZones(metaFiltersData), [metaFiltersData])
  const pricingPeriods = useMemo(() => selectPricingPeriods(metaFiltersData), [metaFiltersData])
  const taxZones = useMemo(() => selectTaxZones(metaFiltersData), [metaFiltersData])
  const recurrencesById = useMemo(() => keyBy(selectRecurrenceOptions(metaFiltersData), 'id'), [metaFiltersData])

  const treeData = useMemo(() => {
    const versions: ServiceVersionRow[] = csVersionsData?.versions || []
    const eventsById = eventsVersionData?.events || {}

    const builder = new ActivityTreeBuilder()

    builder.registerCustomBuilder('serviceEvents', serviceEventsBuilder)
    builder.registerCustomBuilder('tags', tagRefBuilder as CustomBuilderFn)
    builder.registerCustomBuilder('workOrderInvoiceStatus', workOrderInvoiceStatusBuilder)

    const processedServiceVersions = versions.map(v => {
      const recurrence = v?.recurrenceId ? recurrencesById[v.recurrenceId] : null
      const derivedRecurrence = (() => {
        if (!v?.recurrenceId) return 'On Request'
        if (recurrence) return getRecurrenceText(recurrence.recurrencePer, recurrence.recurrenceInterval, v?.recurrenceFrequency)
        // Recurrences are user-managed; an older revision may point at one that has since been deleted
        // or edited. Don't misreport as "On Request" — show "Recurring" instead.
        return 'Recurring'
      })()
      return {
        ...v,
        derivedStatus: (() => {
          const s = (v?.status || '').toString().toUpperCase()
          if (!s) return 'None'
          if (s === 'ACTIVE') return 'Active'
          if (s === 'DEACTIVATED' || s === 'INACTIVE') return 'Deactivated'
          return s.charAt(0) + s.slice(1).toLowerCase()
        })(),
        derivedRecurrence,
      }
    })

    const SERVICE_FIELD_CONFIG = {
      billingStartDate: 'Billing start date',
      billingEndDate: 'Billing end date',
      billingName: 'Billing name',
      deactivateOnEndDate: 'Deactivate on end date',
      endDate: 'End date',
      materialId: 'Material',
      measureId: 'Measure',
      pricingPeriodId: 'Pricing period',
      pricingZoneId: 'Pricing zone',
      purchaseOrderNumber: 'PO Number',
      serviceMethodId: 'Method',
      servicePrice: 'Price',
      startDate: 'Start date',
      taxZoneId: 'Tax zone',
      derivedStatus: 'Status',
      derivedRecurrence: 'Recurrence',
    }

    builder.addChanges(processedServiceVersions, {
      entityName: 'Service',
      fieldConfig: SERVICE_FIELD_CONFIG,
    })

    builder.runCustomBuilder('serviceEvents', {
      eventsById,
      options: { actions, measures, materials, methods, pricingZones, pricingPeriods, taxZones },
    })
    builder.runCustomBuilder('tags', { tagRefsData })
    builder.runCustomBuilder('workOrderInvoiceStatus', { workOrdersById: workOrderVersionsData?.workOrders })

    const postProcessors = [{ run: recurringDaysUnionBuilder as TreePostProcessor['run'], payload: { eventsById } }]

    return builder.buildTree(postProcessors)
  }, [
    csVersionsData,
    eventsVersionData,
    workOrderVersionsData,
    measures,
    methods,
    materials,
    actions,
    pricingZones,
    pricingPeriods,
    taxZones,
    tagRefsData,
    recurrencesById,
  ])

  const isLoading = isCSVLoading || isMetaLoading || isEventsDataLoading || isTagsLoading || isWorkOrdersLoading

  const lookups = useMemo(
    () => ({ measures, methods, materials, actions, pricingZones, pricingPeriods, taxZones }),
    [measures, methods, materials, actions, pricingZones, pricingPeriods, taxZones]
  )

  const formatterRegistry = useMemo(
    () => ({
      materialId: formatMaterial,
      measureId: formatMeasure,
      pricingPeriodId: formatPricingPeriod,
      pricingZoneId: formatPricingZone,
      serviceMethodId: formatMethod,
      taxZoneId: formatTaxZone,
      serviceActionId: formatAction,
      servicePrice: formatMoney,

      startDate: formatDateOnly,
      endDate: formatDateOnly,
      billingStartDate: formatDateOnly,
      billingEndDate: formatDateOnly,

      containerName: formatDefault,
      geoLocation: formatDefault,

      eventRemoved: formatDefault,
      'event.removed': formatDefault,
      deactivateOnEndDate: formatBoolean,
      deleted: formatBoolean,
      customerRequired: formatBoolean,
      actionType: formatAction,
      invoiceStatus: formatInvoiceStatus,
      dayOfTheWeek: formatDefault,
      dayOfTheMonth: formatDefault,
      eventOccurrence: formatDefault,
      'event.schedule': formatDefault,
      'event.days': formatDefault,

      derivedStatus: formatDefault,
      derivedRecurrence: formatDefault,
    }),
    []
  )

  const value = useMemo(() => ({ treeData, isLoading, lookups, formatterRegistry }), [treeData, isLoading, lookups, formatterRegistry])

  return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>
}

export default ActivityProvider
